import React, { useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { Box, Grid, Typography } from "@mui/material";
import { Add } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Layout } from "../Layouts/Layout";
import { BlockingLoader } from "../Components/BlockingLoader";
import { useErrorBoundary } from "../Contexts/ErrorContext";
import {
  GET_SUBMISSION_REVIEW_ACTIVITIES,
  CREATE_SUBMISSION_REVIEW_ACTIVITY,
  DELETE_SUBMISSION_REVIEW_ACTIVITY,
  CLOSE_SUBMISSIONS,
  CLOSE_REVIEWS,
  RELEASE_RESULTS,
  SEND_SUBMISSION_REMINDERS,
  SEND_REVIEW_REMINDERS,
} from "../Queries";
import { SubmissionReviewActivity, SubmissionReviewStatus } from "../types";
import { ActivityCard } from "../Components/SubmissionReview/ActivityCard";

// ── Activity Group (matches CampaignGroup pattern) ───────────────────────────

interface ActivityGroupProps {
  activities: SubmissionReviewActivity[];
  label: string;
  statuses: SubmissionReviewStatus[];
  onTransitionStart: (msg: string) => void;
  onTransitionEnd: () => void;
}

const ActivityGroup = ({
  activities,
  label,
  statuses,
  onTransitionStart,
  onTransitionEnd,
}: ActivityGroupProps) => {
  const filtered = activities.filter((a) => statuses.includes(a.status));

  if (filtered.length === 0) return null;

  return (
    <Box sx={{ mb: 6 }}>
      <Typography
        variant="h5"
        color="primary"
        sx={{ fontWeight: "bold", mb: 2 }}
      >
        {label}
      </Typography>
      <Grid container spacing={2}>
        {filtered.map((activity) => (
          <Grid item key={activity.id} xs={12} md={6} lg={4}>
            <ActivityCard
              activity={activity}
              onChange={() => {}}
              onDelete={() => {}}
              onRemind={() => {}}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// ── Main Page ────────────────────────────────────────────────────────────────

export function SubmissionReviews() {
  const navigate = useNavigate();
  const [t] = useTranslation();
  const { setError } = useErrorBoundary();

  const [addBusy, setAddBusy] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionMessage, setTransitionMessage] = useState("");

  const { loading, data, refetch } = useQuery(
    GET_SUBMISSION_REVIEW_ACTIVITIES,
    {
      // Serve the cached list instantly but always revalidate — activities
      // created on the edit page must appear when navigating back here.
      fetchPolicy: "cache-and-network",
      onError: (error) =>
        setError(String(t("Error loading submissions")), error),
    },
  );

  const [createActivity] = useMutation(CREATE_SUBMISSION_REVIEW_ACTIVITY, {
    refetchQueries: [{ query: GET_SUBMISSION_REVIEW_ACTIVITIES }],
    onCompleted: (result) => {
      setAddBusy(false);
      const id = result.createSubmissionReviewActivity.id;
      navigate(`/submissions/${id}`);
    },
    onError: (error) => {
      setAddBusy(false);
      setError(String(t("Error creating activity")), error);
    },
  });

  const activities: SubmissionReviewActivity[] =
    data?.submissionReviewActivities ?? [];

  const showLoading = loading && activities.length === 0;

  const handleAddActivity = () => {
    setAddBusy(true);
    createActivity({
      variables: {
        input: { name: "Untitled Submission Review" },
      },
    });
  };

  function handleTransitionStart(msg: string) {
    setIsTransitioning(true);
    setTransitionMessage(msg);
  }

  function handleTransitionEnd() {
    setIsTransitioning(false);
    setTransitionMessage("");
    refetch();
  }

  return (
    <Layout
      breadcrumbs={[
        { title: String(t("navigation.submissions")), link: "/submissions" },
      ]}
      loading={showLoading}
    >
      <BlockingLoader open={isTransitioning} message={transitionMessage} />
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <LoadingButton
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddActivity}
            loading={addBusy}
          >
            {t("submissionReviews.add")}
          </LoadingButton>
        </Box>

        <ActivityGroup
          activities={activities}
          label={String(t("Submissions Open"))}
          statuses={[SubmissionReviewStatus.SubmissionsOpen]}
          onTransitionStart={handleTransitionStart}
          onTransitionEnd={handleTransitionEnd}
        />
        <ActivityGroup
          activities={activities}
          label={String(t("Reviews Open"))}
          statuses={[SubmissionReviewStatus.ReviewsOpen]}
          onTransitionStart={handleTransitionStart}
          onTransitionEnd={handleTransitionEnd}
        />
        <ActivityGroup
          activities={activities}
          label={String(t("Draft"))}
          statuses={[SubmissionReviewStatus.Draft]}
          onTransitionStart={handleTransitionStart}
          onTransitionEnd={handleTransitionEnd}
        />
        <ActivityGroup
          activities={activities}
          label={String(t("Closed"))}
          statuses={[
            SubmissionReviewStatus.SubmissionsClosed,
            SubmissionReviewStatus.ReviewsClosed,
            SubmissionReviewStatus.ResultsReleased,
          ]}
          onTransitionStart={handleTransitionStart}
          onTransitionEnd={handleTransitionEnd}
        />
      </Box>
    </Layout>
  );
}
